var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { SPACING, RADIUS } from '@backrs_ui/common-ui/src/designSystem/spacing/spacing';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';
ChartJS.register(CategoryScale, LinearScale, BarElement, annotationPlugin);
var BAR_CHART_HEIGHT = 24;
var AVERAGE_LINE_WIDTH = 12;
var ChartTicks = function (_a) {
    var chartMin = _a.chartMin, chartMax = _a.chartMax, ticks = _a.ticks;
    return (_jsx(Box, { sx: ticksContainerSx, children: ticks.map(function (tick) {
            var positionPercent = ((tick - chartMin) / (chartMax - chartMin)) * 100;
            var isFirstTick = tick === ticks[0];
            var isLastTick = tick === ticks[ticks.length - 1];
            var getTransform = function () {
                if (isFirstTick) {
                    return 'translateX(0)';
                }
                if (isLastTick) {
                    return 'translateX(-100%)';
                }
                return 'translateX(-50%)';
            };
            var getTextAlign = function () {
                if (isFirstTick) {
                    return 'left';
                }
                if (isLastTick) {
                    return 'right';
                }
                return 'center';
            };
            return (_jsx(Box, { sx: __assign(__assign({}, individualTickContainerSx), { left: "".concat(positionPercent, "%"), transform: getTransform(), textAlign: getTextAlign() }), children: _jsx(Typography, { variant: "text-xs-regular", color: function (_a) {
                        var backrs = _a.backrs;
                        return backrs.colors['Colors/Text/text-tertiary'];
                    }, children: tick.toFixed(tick > 0 && tick < 5 && ticks[ticks.length - 1] < 10 ? 1 : 0) }) }, tick));
        }) }));
};
var tooltipArrow = "<div id=\"tooltip-arrow\" style=\"\n              position: absolute;\n              width: 0;\n              height: 0;\n              left: 50%;\n              bottom: -5px;\n              transform: translateX(-50%);\n              border-left: 6px solid transparent;\n              border-right: 6px solid transparent;\n              border-top: 6px solid #0D0D0D;\">\n            </div>";
export var BarChart = function (_a) {
    var chartMin = _a.chartMin, chartMax = _a.chartMax, ticks = _a.ticks, dataMin = _a.dataMin, dataAverage = _a.dataAverage, dataMax = _a.dataMax;
    var isMobile = useMediaQuery('(max-width: 600px)');
    var data = {
        labels: [''],
        datasets: [
            {
                label: '',
                data: [[dataMax, dataMin]],
                backgroundColor: COLOR_TOKENS['Brand/500'],
                borderRadius: 6,
                borderWidth: 1,
                borderColor: COLOR_TOKENS['Colors/Base/Black'],
                borderSkipped: false,
                barThickness: BAR_CHART_HEIGHT,
            },
        ],
    };
    var options = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                min: chartMin,
                max: chartMax,
                display: false,
                grid: { display: false },
                border: { display: false },
            },
            y: {
                display: false,
                grid: { display: false },
                border: { display: false },
                ticks: { padding: 0 },
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: false,
                external: function (context) {
                    var tooltip = context.tooltip;
                    var tooltipEl = document.getElementById('custom-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'custom-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = COLOR_TOKENS['Colors/Base/Black'];
                        tooltipEl.style.color = COLOR_TOKENS['Colors/Base/White'];
                        tooltipEl.style.padding = "".concat(SPACING['spacing-sm'], " ").concat(SPACING['spacing-lg']);
                        tooltipEl.style.borderRadius = RADIUS['radius-md'];
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.textAlign = 'center';
                        tooltipEl.style.zIndex = '999';
                        tooltipEl.innerHTML = tooltipArrow;
                        document.body.appendChild(tooltipEl);
                    }
                    if (tooltip.opacity === 0) {
                        tooltipEl.style.opacity = '0';
                    }
                    else {
                        tooltipEl.innerHTML = "".concat(tooltipArrow, "<div style=\"font-size: 12px; padding-top: 4px; padding-bottom: 4px;\">\n            <p style=\"margin: 0;\">50th percentile: ").concat(dataAverage, "</p>\n            </div>");
                        var chartRect = context.chart.canvas.getBoundingClientRect();
                        var tooltipX = chartRect.left +
                            context.chart.scales.x.getPixelForValue(dataAverage) -
                            tooltipEl.offsetWidth / 2;
                        var tooltipY = chartRect.top +
                            context.chart.scales.y.getPixelForValue(0) -
                            tooltipEl.offsetHeight -
                            20;
                        tooltipEl.style.left = "".concat(tooltipX, "px");
                        tooltipEl.style.top = "".concat(tooltipY, "px");
                        tooltipEl.style.opacity = '1';
                    }
                },
            },
            annotation: {
                annotations: dataAverage === 0
                    ? []
                    : {
                        averageLine: {
                            type: 'line',
                            borderColor: COLOR_TOKENS['Colors/Base/Black'],
                            borderWidth: AVERAGE_LINE_WIDTH * (isMobile ? 0.5 : 1),
                            scaleID: 'x',
                            value: dataAverage,
                        },
                    },
            },
        },
    };
    return (_jsxs(Box, { sx: chartContainerSx, children: [_jsx(Box, { sx: lineChartContainerSx, children: _jsx(Bar, { data: data, options: options, height: BAR_CHART_HEIGHT - 1, width: 300 }) }), _jsx(ChartTicks, { chartMin: chartMin, chartMax: chartMax, ticks: ticks })] }));
};
var ticksContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        position: 'relative',
        marginTop: backrs.spacing['spacing-2xl'],
    });
};
var individualTickContainerSx = {
    position: 'absolute',
};
var chartContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        height: "".concat(backrs.spacing['spacing-7xl']),
        width: '100%',
    });
};
var lineChartContainerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        width: '100%',
        height: "".concat(BAR_CHART_HEIGHT, "px"),
        overflow: 'hidden',
        border: "1px solid ".concat(backrs.colors['Colors/Base/Black']),
        backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
        borderRadius: backrs.radius['radius-sm'],
        position: 'relative',
        boxSizing: 'border-box',
    });
};
